import React, { useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  styled,
  keyframes,
} from '@mui/material';
import { LiveStreamEmoji } from '../../services/live-stream-service';
import liveStreamService from '../../services/live-stream-service';

/**
 * Animation for button press feedback
 */
const buttonPress = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
`;

/**
 * Animation for rate limit feedback
 */
const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
`;

/**
 * Styled container for emoji buttons
 */
const EmojiButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  borderRadius: theme.spacing(2),
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

/**
 * Styled emoji button
 */
const EmojiButton = styled(IconButton, {
  shouldForwardProp: (prop) => !['isPressed', 'isRateLimited'].includes(prop as string),
})<{ 
  isPressed?: boolean; 
  isRateLimited?: boolean;
}>(({ theme, isPressed, isRateLimited }) => ({
  fontSize: '1.5rem',
  padding: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  transition: 'all 0.2s ease',
  animation: isPressed 
    ? `${buttonPress} 0.2s ease`
    : isRateLimited 
    ? `${shake} 0.5s ease`
    : 'none',
  
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'scale(1.1)',
  },
  
  '&:disabled': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.3)',
    cursor: 'not-allowed',
  },
}));

/**
 * Emoji configuration with labels
 */
const EMOJI_CONFIG = [
  { emoji: LiveStreamEmoji.HEART, label: 'Love it!' },
  { emoji: LiveStreamEmoji.FIRE, label: 'Fire!' },
  { emoji: LiveStreamEmoji.CLAP, label: 'Amazing!' },
  { emoji: LiveStreamEmoji.WOW, label: 'Wow!' },
  { emoji: LiveStreamEmoji.LAUGH, label: 'Funny!' },
  { emoji: LiveStreamEmoji.THUMBS_UP, label: 'Like!' },
  { emoji: LiveStreamEmoji.STAR, label: 'Star!' },
  { emoji: LiveStreamEmoji.FASHION, label: 'Fashionable!' },
];

/**
 * Props for EmojiReactionButtons component
 */
interface EmojiReactionButtonsProps {
  /** Stream ID for the current stream */
  streamId: string;
  /** Whether the user is currently watching the stream */
  isWatching: boolean;
  /** Whether emoji reactions are enabled */
  enabled?: boolean;
  /** Callback when emoji is sent successfully */
  onEmojiSent?: (emoji: LiveStreamEmoji) => void;
  /** Callback when emoji sending fails */
  onEmojiError?: (error: string) => void;
  /** Custom styling */
  sx?: any;
}

/**
 * Emoji reaction buttons component for live streams
 * Provides UI for sending emoji reactions with rate limiting
 */
const EmojiReactionButtons: React.FC<EmojiReactionButtonsProps> = ({
  streamId,
  isWatching,
  enabled = true,
  onEmojiSent,
  onEmojiError,
  sx,
}) => {
  const [pressedButton, setPressedButton] = useState<LiveStreamEmoji | null>(null);
  const [rateLimitedButton, setRateLimitedButton] = useState<LiveStreamEmoji | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastEmojiTime, setLastEmojiTime] = useState<number>(0);

  /**
   * Rate limiting check (1 emoji per second)
   */
  const isRateLimited = useCallback(() => {
    const now = Date.now();
    const timeSinceLastEmoji = now - lastEmojiTime;
    return timeSinceLastEmoji < 1000; // 1 second rate limit
  }, [lastEmojiTime]);

  /**
   * Handle emoji button click
   */
  const handleEmojiClick = useCallback(async (emoji: LiveStreamEmoji) => {
    if (!isWatching) {
      setError('You must be watching the stream to send emoji reactions');
      return;
    }

    if (!enabled) {
      setError('Emoji reactions are currently disabled');
      return;
    }

    if (isLoading) {
      return; // Prevent multiple simultaneous requests
    }

    // Check rate limiting
    if (isRateLimited()) {
      setRateLimitedButton(emoji);
      setError('Please wait before sending another emoji reaction');
      
      // Clear rate limited state after animation
      setTimeout(() => {
        setRateLimitedButton(null);
      }, 500);
      
      return;
    }

    try {
      setIsLoading(true);
      setPressedButton(emoji);
      
      // Send emoji to backend
      await liveStreamService.sendEmoji(streamId, emoji);
      
      // Update last emoji time
      setLastEmojiTime(Date.now());
      
      // Call success callback
      if (onEmojiSent) {
        onEmojiSent(emoji);
      }
      
      // Clear pressed state after animation
      setTimeout(() => {
        setPressedButton(null);
      }, 200);
      
    } catch (error: any) {
      console.error('Failed to send emoji reaction:', error);
      
      let errorMessage = 'Failed to send emoji reaction';
      
      // Handle specific error types
      if (error.response?.status === 429) {
        errorMessage = 'Rate limit exceeded. Please wait before sending another emoji.';
        setRateLimitedButton(emoji);
        setTimeout(() => setRateLimitedButton(null), 500);
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.error?.message || 'Invalid emoji reaction';
      } else if (error.response?.status === 401) {
        errorMessage = 'You must be logged in to send emoji reactions';
      } else if (error.response?.status === 404) {
        errorMessage = 'Stream not found or has ended';
      }
      
      setError(errorMessage);
      
      // Call error callback
      if (onEmojiError) {
        onEmojiError(errorMessage);
      }
      
      setPressedButton(null);
    } finally {
      setIsLoading(false);
    }
  }, [
    streamId,
    isWatching,
    enabled,
    isLoading,
    isRateLimited,
    onEmojiSent,
    onEmojiError,
  ]);

  /**
   * Close error snackbar
   */
  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get button disabled state
   */
  const isButtonDisabled = useCallback((emoji: LiveStreamEmoji) => {
    return !isWatching || !enabled || isLoading || pressedButton === emoji;
  }, [isWatching, enabled, isLoading, pressedButton]);

  if (!isWatching) {
    return null; // Don't show buttons if not watching
  }

  return (
    <>
      <EmojiButtonContainer sx={sx}>
        {EMOJI_CONFIG.map(({ emoji, label }) => (
          <Tooltip key={emoji} title={label} placement="left">
            <span>
              <EmojiButton
                onClick={() => handleEmojiClick(emoji)}
                disabled={isButtonDisabled(emoji)}
                isPressed={pressedButton === emoji}
                isRateLimited={rateLimitedButton === emoji}
                aria-label={`Send ${label} emoji reaction`}
              >
                {emoji}
              </EmojiButton>
            </span>
          </Tooltip>
        ))}
      </EmojiButtonContainer>

      {/* Error snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseError} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmojiReactionButtons;